import React, { useState, useEffect } from 'react';
import {
	Card,
	CardContent,
	Typography,
	CircularProgress,
	Alert,
	Box,
	Chip,
	Button,
	Divider,
	Paper,
	Grid,
	TextField,
	FormControlLabel,
	Switch,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import PublicIcon from '@mui/icons-material/Public';
import GroupIcon from '@mui/icons-material/Group';
import { Title, useNotify } from 'react-admin';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
	Template,
	getTemplate,
	updateTemplate,
	deleteTemplate,
	indexUseCaseToQdrant,
} from '../../Services/quantiAI';

export const AIUseCaseDetail = () => {
	const { code: slug } = useParams<{ code: string }>();
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const notify = useNotify();
	const [template, setTemplate] = useState<Template | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [editing, setEditing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [deleting, setDeleting] = useState(false);

	// Form state
	const [formData, setFormData] = useState<Partial<Template>>({});
	const [skusText, setSkusText] = useState('');
	const [semanticRequiredText, setSemanticRequiredText] = useState('');
	const [semanticOptionalText, setSemanticOptionalText] = useState('');
	const [tagsText, setTagsText] = useState('');

	const initTextFields = (data: Partial<Template>) => {
		setSkusText(data.required_skus?.join(', ') || '');
		setSemanticRequiredText(data.semantic_codes?.required?.join(', ') || '');
		setSemanticOptionalText(data.semantic_codes?.optional?.join(', ') || '');
		setTagsText(data.tags?.join(', ') || '');
	};

	useEffect(() => {
		const loadTemplate = async () => {
			if (!slug) return;
			setLoading(true);
			setError(null);
			try {
				const response = await getTemplate(slug);
				setTemplate(response.data);
				setFormData(response.data);
				if (searchParams.get('edit') === 'true') {
					initTextFields(response.data);
					setEditing(true);
					setSearchParams({}, { replace: true });
				}
			} catch (err: any) {
				setError(err.message || 'Impossible de charger le use case');
			} finally {
				setLoading(false);
			}
		};
		loadTemplate();
	}, [slug]);

	const handleEdit = () => {
		setFormData(template || {});
		initTextFields(template || {});
		setEditing(true);
	};

	const handleCancel = () => {
		setFormData(template || {});
		initTextFields(template || {});
		setEditing(false);
	};

	const handleSave = async () => {
		if (!slug || !template) return;
		setSaving(true);
		try {
			const parseCSV = (text: string) =>
				text
					.split(',')
					.map((s) => s.trim())
					.filter(Boolean);
			const dataToSave = {
				...formData,
				required_skus: parseCSV(skusText),
				tags: parseCSV(tagsText),
				semantic_codes: {
					required: parseCSV(semanticRequiredText),
					optional: parseCSV(semanticOptionalText),
				},
			};
			const response = await updateTemplate(slug, dataToSave);
			setTemplate(response.data);
			setEditing(false);
			notify('Use case mis à jour', { type: 'success' });

			// Sync to Qdrant after save
			try {
				await indexUseCaseToQdrant(template.id);
				notify('Indexé dans Qdrant', { type: 'info' });
			} catch (qdrantErr: any) {
				notify(`Attention: non indexé dans Qdrant - ${qdrantErr.message}`, {
					type: 'warning',
				});
			}
		} catch (err: any) {
			notify(err.message || 'Erreur lors de la mise à jour', { type: 'error' });
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!slug) return;
		setDeleting(true);
		try {
			await deleteTemplate(slug);
			notify('Use case supprimé', { type: 'success' });
			navigate('/ai-use-cases');
		} catch (err: any) {
			notify(err.message || 'Erreur lors de la suppression', { type: 'error' });
		} finally {
			setDeleting(false);
			setDeleteDialogOpen(false);
		}
	};

	const updateForm = (field: string, value: any) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};


	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Card>
				<CardContent>
					<Alert severity="error">{error}</Alert>
					<Button
						startIcon={<ArrowBackIcon />}
						onClick={() => navigate('/ai-use-cases')}
						sx={{ mt: 2 }}
					>
						Retour à la liste
					</Button>
				</CardContent>
			</Card>
		);
	}

	if (!template) {
		return (
			<Card>
				<CardContent>
					<Alert severity="warning">Use case non trouvé</Alert>
					<Button
						startIcon={<ArrowBackIcon />}
						onClick={() => navigate('/ai-use-cases')}
						sx={{ mt: 2 }}
					>
						Retour à la liste
					</Button>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<Title title={`Use Case - ${template.name}`} />
			<CardContent>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
					<Button
						startIcon={<ArrowBackIcon />}
						onClick={() => navigate('/ai-use-cases')}
					>
						Retour à la liste
					</Button>
					<Box sx={{ display: 'flex', gap: 1 }}>
						{editing ? (
							<>
								<Button
									variant="outlined"
									startIcon={<CancelIcon />}
									onClick={handleCancel}
									disabled={saving}
								>
									Annuler
								</Button>
								<Button
									variant="contained"
									color="primary"
									startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
									onClick={handleSave}
									disabled={saving}
								>
									Enregistrer
								</Button>
							</>
						) : (
							<>
								<Button
									variant="outlined"
									color="error"
									startIcon={<DeleteIcon />}
									onClick={() => setDeleteDialogOpen(true)}
								>
									Supprimer
								</Button>
								<Button
									variant="contained"
									startIcon={<EditIcon />}
									onClick={handleEdit}
								>
									Modifier
								</Button>
							</>
						)}
					</Box>
				</Box>

				{editing ? (
					// Edit mode
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
						<TextField
							label="Nom"
							value={formData.name || ''}
							onChange={(e) => updateForm('name', e.target.value)}
							fullWidth
						/>

						<Box sx={{ display: 'flex', gap: 2 }}>
							<TextField
								label="Slug"
								value={formData.slug || ''}
								disabled
								sx={{ flex: 1 }}
								helperText="Le slug ne peut pas être modifié"
							/>
							<TextField
								label="Catégorie"
								value={formData.category || ''}
								onChange={(e) => updateForm('category', e.target.value)}
								sx={{ flex: 1 }}
							/>
							<TextField
								label="Difficulté"
								value={formData.difficulty || ''}
								onChange={(e) => updateForm('difficulty', e.target.value)}
								sx={{ flex: 1 }}
							/>
						</Box>

						<TextField
							label="Description"
							value={formData.description || ''}
							onChange={(e) => updateForm('description', e.target.value)}
							fullWidth
							multiline
							rows={3}
						/>

						<TextField
							label="Tags (séparés par des virgules)"
							value={tagsText}
							onChange={(e) => setTagsText(e.target.value)}
							fullWidth
						/>

						<Divider />

						<Typography variant="h6">Semantic Codes</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<TextField
									label="Requis (séparés par des virgules)"
									value={semanticRequiredText}
									onChange={(e) => setSemanticRequiredText(e.target.value)}
									fullWidth
									multiline
									rows={2}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									label="Optionnels (séparés par des virgules)"
									value={semanticOptionalText}
									onChange={(e) => setSemanticOptionalText(e.target.value)}
									fullWidth
									multiline
									rows={2}
								/>
							</Grid>
						</Grid>

						<TextField
							label="SKUs Requis (séparés par des virgules)"
							value={skusText}
							onChange={(e) => setSkusText(e.target.value)}
							fullWidth
						/>

						<Divider />

						<Typography variant="h6">SQL Template</Typography>
						<TextField
							value={formData.sql_template || ''}
							onChange={(e) => updateForm('sql_template', e.target.value)}
							fullWidth
							multiline
							rows={10}
							sx={{ fontFamily: 'monospace' }}
						/>

						<Typography variant="h6">Prompt</Typography>
						<TextField
							value={formData.prompt || ''}
							onChange={(e) => updateForm('prompt', e.target.value)}
							fullWidth
							multiline
							rows={10}
							placeholder="Instructions structurées pour les use cases sans SQL (ex: étapes d'analyse, guidelines de reporting...)"
						/>

						<Divider />

						<Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
							{formData.scope && formData.scope !== 'global' && (
								<Button
									variant="outlined"
									size="small"
									startIcon={<PublicIcon />}
									onClick={() => {
										updateForm('scope', 'global');
										updateForm('project_folder_id', undefined);
									}}
								>
									Passer en global
								</Button>
							)}
							<FormControlLabel
								control={
									<Switch
										checked={formData.status === 'published'}
										onChange={(e) => updateForm('status', e.target.checked ? 'published' : 'draft')}
									/>
								}
								label="Publié"
							/>
							<Tooltip title="Les use cases featured remontent en priorité dans les résultats RAG" arrow>
								<FormControlLabel
									control={
										<Switch
											checked={formData.is_featured || false}
											onChange={(e) => updateForm('is_featured', e.target.checked)}
										/>
									}
									label="Featured"
								/>
							</Tooltip>
						</Box>
					</Box>
				) : (
					// View mode
					<>
						<Box sx={{ mb: 3 }}>
							<Typography variant="h5" gutterBottom>
								{template.name}
							</Typography>
							<Typography variant="subtitle2" color="text.secondary">
								<code>{template.slug}</code>
							</Typography>
						</Box>

						<Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
							<Chip
								icon={template.scope === 'global' ? <PublicIcon /> : <GroupIcon />}
								label={template.scope === 'global' ? 'Global' : `Projet${template.project_folder_id ? ` (${template.project_folder_id})` : ''}`}
								variant="outlined"
							/>
							{template.category && (
								<Chip label={template.category} color="primary" />
							)}
							{template.difficulty && <Chip label={template.difficulty} />}
							{template.status === 'published' ? (
								<Chip label="Publié" color="success" />
							) : (
								<Chip label="Brouillon" />
							)}
							{template.is_featured && <Chip label="Featured" color="secondary" />}
						</Box>

						{template.tags && template.tags.length > 0 && (
							<Box sx={{ mb: 3 }}>
								<Typography variant="subtitle2" color="text.secondary" gutterBottom>
									Tags
								</Typography>
								<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
									{template.tags.map((tag) => (
										<Chip key={tag} label={tag} size="small" variant="outlined" />
									))}
								</Box>
							</Box>
						)}

						<Divider sx={{ my: 3 }} />

						{template.description && (
							<Box sx={{ mb: 3 }}>
								<Typography variant="h6" gutterBottom>
									Description
								</Typography>
								<Typography>{template.description}</Typography>
							</Box>
						)}

						{/* Semantic Codes */}
						{template.semantic_codes && (
							<Box sx={{ mb: 3 }}>
								<Typography variant="h6" gutterBottom>
									Semantic Codes
								</Typography>
								<Grid container spacing={2}>
									{template.semantic_codes.required?.length > 0 && (
										<Grid item xs={12} md={6}>
											<Typography variant="subtitle2" color="text.secondary">
												Requis
											</Typography>
											<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
												{template.semantic_codes.required.map((code) => (
													<Chip
														key={code}
														label={code}
														size="small"
														color="error"
														variant="outlined"
													/>
												))}
											</Box>
										</Grid>
									)}
									{template.semantic_codes.optional?.length > 0 && (
										<Grid item xs={12} md={6}>
											<Typography variant="subtitle2" color="text.secondary">
												Optionnels
											</Typography>
											<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
												{template.semantic_codes.optional.map((code) => (
													<Chip
														key={code}
														label={code}
														size="small"
														variant="outlined"
													/>
												))}
											</Box>
										</Grid>
									)}
								</Grid>
							</Box>
						)}

						{/* Required SKUs */}
						{template.required_skus && template.required_skus.length > 0 && (
							<Box sx={{ mb: 3 }}>
								<Typography variant="h6" gutterBottom>
									SKUs Requis
								</Typography>
								<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
									{template.required_skus.map((sku) => (
										<Chip key={sku} label={sku} size="small" color="info" />
									))}
								</Box>
							</Box>
						)}

						{/* Output */}
						{template.output && (
							<Box sx={{ mb: 3 }}>
								<Typography variant="h6" gutterBottom>
									Output
								</Typography>
								<Grid container spacing={2}>
									{template.output.dimensions && template.output.dimensions.length > 0 && (
										<Grid item xs={12} md={6}>
											<Typography variant="subtitle2" color="text.secondary">
												Dimensions
											</Typography>
											<Box component="ul" sx={{ m: 0, pl: 2 }}>
												{template.output.dimensions.map((dim) => (
													<li key={dim.code}>
														<code>{dim.code}</code> → {dim.alias}
													</li>
												))}
											</Box>
										</Grid>
									)}
									{template.output.metrics && template.output.metrics.length > 0 && (
										<Grid item xs={12} md={6}>
											<Typography variant="subtitle2" color="text.secondary">
												Métriques
											</Typography>
											<Box component="ul" sx={{ m: 0, pl: 2 }}>
												{template.output.metrics.map((metric) => (
													<li key={metric.code}>
														<code>{metric.code}</code> → {metric.alias}
													</li>
												))}
											</Box>
										</Grid>
									)}
								</Grid>
							</Box>
						)}

						<Divider sx={{ my: 3 }} />

						{template.sql_template && (
							<Box sx={{ mb: 3 }}>
								<Typography variant="h6" gutterBottom>
									SQL Template
								</Typography>
								<Paper
									sx={{
										p: 2,
										bgcolor: 'grey.100',
										fontFamily: 'monospace',
										fontSize: '0.875rem',
										overflow: 'auto',
										maxHeight: 400,
									}}
								>
									<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
										{template.sql_template}
									</pre>
								</Paper>
							</Box>
						)}

						{template.prompt && (
							<Box sx={{ mb: 3 }}>
								<Typography variant="h6" gutterBottom>
									Prompt
								</Typography>
								<Paper
									sx={{
										p: 2,
										bgcolor: 'grey.50',
										fontSize: '0.875rem',
										overflow: 'auto',
										maxHeight: 400,
									}}
								>
									<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
										{template.prompt}
									</pre>
								</Paper>
							</Box>
						)}

						<Divider sx={{ my: 3 }} />

						<Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
							<Box>
								<Typography variant="body2" color="text.secondary">
									Version
								</Typography>
								<Typography>{template.version}</Typography>
							</Box>
							<Box>
								<Typography variant="body2" color="text.secondary">
									Créé le
								</Typography>
								<Typography>
									{new Date(template.created_at).toLocaleDateString('fr-FR')}
								</Typography>
							</Box>
							<Box>
								<Typography variant="body2" color="text.secondary">
									Modifié le
								</Typography>
								<Typography>
									{new Date(template.updated_at).toLocaleDateString('fr-FR')}
								</Typography>
							</Box>
							{template.source_file && (
								<Box>
									<Typography variant="body2" color="text.secondary">
										Fichier source
									</Typography>
									<Typography>
										<code style={{ fontSize: '0.75rem' }}>{template.source_file}</code>
									</Typography>
								</Box>
							)}
						</Box>
					</>
				)}
			</CardContent>

			{/* Delete confirmation dialog */}
			<Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
				<DialogTitle>Confirmer la suppression</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Êtes-vous sûr de vouloir supprimer le use case "{template.name}" ? Cette
						action est irréversible.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
						Annuler
					</Button>
					<Button
						onClick={handleDelete}
						color="error"
						disabled={deleting}
						startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
					>
						Supprimer
					</Button>
				</DialogActions>
			</Dialog>
		</Card>
	);
};
